import React, { MutableRefObject, useState } from "react";
import { Link, Navigate } from "react-router-dom";
import { useTranslation } from "react-i18next";
import { useForm } from "react-hook-form";
import { yupResolver } from "@hookform/resolvers/yup";
import * as yup from "yup";

import { useAppDispatch, useAppSelector } from "../../store/hooks";
import { SolidButton } from "../buttons/SolidButton";
import { UserData } from "pages/SignUpPage";
import { GoogleAuthButton } from "./GoogleAuthButton";
import { IsRunningOverwolf } from "../../utilityFunctions";
import { DiscordLoginButton } from "./DiscordLoginButton";
import googleSvg from "../../assets/svg/google.svg";
import discordSvg from "../../assets/svg/discord-icon.svg";
import { LoginWithToken } from "store/actions/authActions/LoginWithToken";
import { GetRemoteAuthenticationToken } from "store/actions/authActions/GetRemoteAuthenticationToken";
import { RemoteAuthentictionModal } from "pages/RemoteAuthenticationModal";
import { useWindowSize } from "hooks/useWindowSize";
import useApplicationInfo from "hooks/useAppInfo";
import { CapacitorGoogleAuthButton } from "./CapacitorGoogleAuthButton";
import { InputField } from "components/InputField";

interface FormValues {
  email: string;
  firstName: string;
  lastName: string;
  password: string;
  confirmPassword: string;
  referrerEmail: string;
}

interface Props {
  qrTokenToValidate?: string;
  forbiddenError: boolean;
  setForbiddenError: React.Dispatch<React.SetStateAction<boolean>>;
  setEmailInUseError: React.Dispatch<React.SetStateAction<boolean>>;
  setUserData: React.Dispatch<React.SetStateAction<UserData | null>>;
  setGoogleToken: React.Dispatch<React.SetStateAction<string | null>>;
  setDiscordCode: React.Dispatch<React.SetStateAction<string | null>>;
  onlySocial?: boolean;
  stopLoginWithToken: MutableRefObject<boolean>;
  setShowForm: React.Dispatch<React.SetStateAction<boolean>>;
  showForm: boolean;
}

export const UserRegistrationForm: React.FC<Props> = ({
  qrTokenToValidate,
  setEmailInUseError,
  forbiddenError,
  setForbiddenError,
  setUserData,
  setGoogleToken,
  setDiscordCode,
  onlySocial,
  stopLoginWithToken,
  setShowForm,
  showForm,
}) => {
  const [signInRedirect, setSignInRedirect] = useState<boolean>(false);
  const [loading, setLoading] = useState<boolean>(false);
  const dispatch = useAppDispatch();
  const { t } = useTranslation();
  const language = t("localization_testing");
  const [token, setToken] = useState<string>("");
  const remoteSignupInitiated = React.useRef(false);
  const [showRemoteAuthModal, setShowRemoteAuthModal] =
    useState<boolean>(false);
  const [width] = useWindowSize();
  const REACT_APP_WEBAPP_URL = useAppSelector(
    (state) => state?.auth?.env?.REACT_APP_WEBAPP_URL
  );
  const { application } = useApplicationInfo();
  const isRunningIos = application === "IOS_CLIENT";

  const capacitorIsRunning = application === "ANDROID_CLIENT" || isRunningIos;

  const schema = yup.object().shape({
    email: yup
      .string()
      .email(t("form.validEmail"))
      .max(100, t("form.emailTooLong"))
      .required(t("form.validEmail")),
    password: yup
      .string()
      .required(t("form.noPasswordProvided"))
      .max(30, t("form.passwordTooLong"))
      .matches(
        /^(?=.*[a-z])(?=.*[A-Z])(?=.*[0-9])(?=.{10,})/,
        t("form.passwordValidation")
      ),
    confirmPassword: yup
      .string()
      .oneOf([yup.ref("password"), null], t("form.errorConfirmNewPassword"))
      .required(t("form.pleaseConfirmPassword")),
    firstName: yup
      .string()
      .required(t("form.enterFirstName"))
      .min(2, t("form.firstNameTooShort"))
      .max(100, t("form.firstNameTooLong"))
      .matches(
        /^(?:(?![\]^\\×÷])[A-zÀ-ÿ .'-])+$/i,
        t("error.invalidCharFirstName")
      ),
    lastName: yup
      .string()
      .required(t("form.enterLastName"))
      .min(2, t("form.lastNameTooShort"))
      .max(100, t("form.lastNameTooLong"))
      .matches(
        /^(?:(?![\]^\\×÷])[A-zÀ-ÿ .'-])+$/i,
        t("error.invalidCharLastName")
      ),
  });

  const {
    register,
    handleSubmit,
    formState: { errors },
  } = useForm<FormValues>({
    resolver: yupResolver(schema),
  });

  if (!!token && !remoteSignupInitiated.current) {
    remoteSignupInitiated.current = true;
    overwolf.utils.openUrlInDefaultBrowser(
      `${REACT_APP_WEBAPP_URL}/socialSignup/${token}`
    );
    dispatch(
      LoginWithToken(
        language,
        token,
        true,
        stopLoginWithToken,
        setSignInRedirect,
        setLoading,
        setShowRemoteAuthModal
      )
    );
  }

  const remoteOverwolfSignupHandler = () => {
    setToken("");
    remoteSignupInitiated.current = false;
    stopLoginWithToken.current = false;
    setShowRemoteAuthModal(true);
    dispatch(GetRemoteAuthenticationToken(setLoading, setToken));
  };

  if (signInRedirect && !!qrTokenToValidate)
    return <Navigate replace to="/done" />;
  if (signInRedirect && !qrTokenToValidate && width > 640) {
    return <Navigate replace to="/dashboard/mymeals" />;
  }
  if (signInRedirect && !qrTokenToValidate && width <= 640) {
    return <Navigate replace to="/confirmation" />;
  }

  return (
    <>
      <RemoteAuthentictionModal
        showModal={showRemoteAuthModal}
        setShowModal={setShowRemoteAuthModal}
        stopLoginWithToken={stopLoginWithToken}
        setLoading={setLoading}
      />
      {!!IsRunningOverwolf() ? (
        <div className="w-full mx-auto pb-1 space-y-1">
          <SolidButton
            onClick={() => remoteOverwolfSignupHandler()}
            blackText
            backgroundColor="bg-blue-200"
            hoverColor="hover:bg-blue-300"
            width="w-full"
            disabled={loading}
            loading={loading}
          >
            <div className="flex space-x-2">
              <span>{t("form.continueWith")}</span>
              <img className="h-5" src={googleSvg} alt="google" />
              <span>{t("form.or")}</span>
              <img className="h-5" src={discordSvg} alt="discord" />
            </div>
          </SolidButton>
        </div>
      ) : (
        <div
          className={`${
            showForm
              ? onlySocial
                ? "w-60 mx-auto space-y-1"
                : "w-full space-y-2 md:space-y-0 md:flex justify-center items-center md:space-x-4 mx-auto"
              : "w-60 mx-auto space-y-1"
          } pb-2`}
        >
          <div className={`${showForm ? "md:w-1/2 flex justify-center" : ""}`}>
            <div
              className={`${showForm ? "w-72 md:w-full lg:w-60 xl:w-64" : ""}`}
            >
              {!capacitorIsRunning && (
                <GoogleAuthButton
                  small={!showForm}
                  setGoogleToken={setGoogleToken}
                  persistentLogin={true}
                  signup={true}
                  setRedirect={setSignInRedirect}
                  qrTokenToValidate={qrTokenToValidate}
                />
              )}
            </div>
          </div>
          {capacitorIsRunning && !isRunningIos && (
            <CapacitorGoogleAuthButton
              setGoogleToken={setGoogleToken}
              persistentLogin={true}
              signup={true}
              setRedirect={setSignInRedirect}
              qrTokenToValidate={qrTokenToValidate}
            />
          )}
          <div className={`${showForm ? "md:w-1/2 flex justify-center" : ""}`}>
            <div
              className={`${showForm ? "w-72 md:w-full lg:w-60 xl:w-64" : ""}`}
            >
              {!capacitorIsRunning && (
                <DiscordLoginButton
                  setDiscordCode={setDiscordCode}
                  persistentLogin={true}
                  signup={true}
                  setRedirect={setSignInRedirect}
                  qrTokenToValidate={qrTokenToValidate}
                />
              )}
            </div>
          </div>
        </div>
      )}
      {!onlySocial && (
        <>
          <div
            className={`w-60 mx-auto pb-2 space-y-1 ${
              width < 767 ? "hidden" : ""
            }`}
          >
            <div className={` ${showForm ? "hidden" : ""}`}>
              <SolidButton
                onClick={() => {
                  setShowForm(true);
                }}
                blackText
                backgroundColor="bg-blue-200"
                hoverColor="hover:bg-blue-300"
                width="w-full"
              >
                <div className="flex items-center space-x-2">
                  <span>{t("form.continueWithEmail")}</span>
                </div>
              </SolidButton>
            </div>
          </div>
          {!!showForm && (
            <form
              className="pb-2 space-y-2 md:space-y-4 2xl:space-y-6"
              onSubmit={handleSubmit((data) => {
                setEmailInUseError(false);
                setForbiddenError(false);
                setUserData(data);
              })}
            >
              <div className="sm:flex justify-between w-full space-y-2 sm:space-y-0 sm:space-x-2 2xl:space-x-4">
                <div className="w-full">
                  <InputField
                    id="firstName"
                    label={t("form.firstName")}
                    inputType="text"
                    placeholder={t("form.firstNamePlaceHolder")}
                    register={register("firstName")}
                    requiredFlag={true}
                  />
                  {!!errors?.firstName && (
                    <small className="text-red-500 text-xs">
                      {errors?.firstName?.message}
                    </small>
                  )}
                </div>
                <div className="w-full">
                  <InputField
                    id="lastName"
                    label={t("form.lastName")}
                    inputType="text"
                    placeholder={t("form.lastNamePlaceHolder")}
                    register={register("lastName")}
                    requiredFlag={true}
                  />
                  {!!errors?.lastName && (
                    <small className="text-red-500 text-xs">
                      {errors?.lastName?.message}
                    </small>
                  )}
                </div>
              </div>
              <div className="w-full">
                <InputField
                  id="email"
                  label={t("form.email")}
                  inputType="email"
                  placeholder={t("form.emailPlaceHolder")}
                  register={register("email")}
                  requiredFlag={true}
                />
                {!!errors?.email && (
                  <small className="text-red-500">
                    {errors?.email?.message}
                  </small>
                )}
              </div>
              <div className="w-full">
                <InputField
                  id="password"
                  label={t("form.password")}
                  inputType="password"
                  placeholder={t("form.passwordPlaceHolder")}
                  register={register("password")}
                  requiredFlag={true}
                  password
                />
                {!!errors?.password && (
                  <small className="text-red-500">
                    {errors?.password?.message}
                  </small>
                )}
              </div>
              <div className="w-full">
                <InputField
                  id="confirmPassword"
                  label={t("form.confirmPassword")}
                  inputType="password"
                  placeholder={t("form.confirmPasswordPlaceHolder")}
                  register={register("confirmPassword")}
                  requiredFlag={true}
                  password
                />
                {!!errors?.confirmPassword && (
                  <small className="text-red-500">
                    {errors?.confirmPassword?.message}
                  </small>
                )}
              </div>
              <div className="pt-2 2xl:pt-0">
                <div className="text-center">
                  <small className="text-slate-400">
                    {t("signUpAgreement")}
                    <Link to="/terms" draggable={false}>
                      <span className="text-blue-500 hover:underline px-1">
                        {t("termsOfService")?.toLowerCase()}
                      </span>
                    </Link>
                    {language === "fr" ? "et" : "and"}
                    <Link to="/privacy" draggable={false}>
                      <span className="text-blue-500 hover:underline px-1">
                        {`${t("privacyPolicy")?.toLowerCase()}.`}
                      </span>
                    </Link>
                  </small>
                </div>
                <div className="pt-2 space-y-2">
                  <SolidButton
                    squared
                    width="w-full"
                    type="submit"
                    disabled={loading}
                    loading={loading}
                  >
                    <span className="flex items-center">
                      {t("form.signUp")}
                    </span>
                  </SolidButton>
                </div>
              </div>

              {forbiddenError && (
                <small className="text-red-500">
                  {t("error.forbiddenSignUp")}
                </small>
              )}
            </form>
          )}
        </>
      )}
    </>
  );
};
